import { Events } from '@/models/events.model'
import { Artifact, asSnapshotReference } from '@/models'

import GraphState from './../rappid/graph-state'

import { useRoute } from 'vue-router'
import { freezeWrap, tryWrap, useEventBus } from '@/utils/events'
import { useTraceDataStore } from '@/stores/traceDataStore'

import { parseIntUndefined } from '@/utils/helpers'
import { injectTyped } from '@/inject'
import { ServiceKeys } from '@/services/symbols'

export function useRegisterLoadMorePill() {
  const route = useRoute()
  const traceDataStore = useTraceDataStore()

  const rappidContext = injectTyped(ServiceKeys.rappidContext)
  const artifactService = injectTyped(ServiceKeys.artifact)

  let graphState: GraphState | undefined = undefined

  async function handler(event: Events['loadArtifactsFromApiAfterPillPress']) {
    if (!graphState || !route.params.projectId || !route.params.snapshotId) {
      throw Error('Missing data for rendering tracegraph')
    }

    if (!event.searchMode) {
      if (event.artifactType.loadedPages != undefined) {
        event.artifactType.loadedPages++
        await traceDataStore.loadArtifactsCollectionFromApi({
          artifactService: artifactService,
          projectId: parseInt(route.params.projectId as string),
          snapshotId: asSnapshotReference(route.params.snapshotId),
          typeId: event.artifactType.id,
          filterId: parseIntUndefined(route.query.filter as string),
          //include: 'numberOfLinksAsA, numberofLinksAsB',
          sortBy: 'LINK',
          page: event.artifactType.loadedPages,
          pageSize: 15,
        })
      }

      await Promise.all(
        traceDataStore.modelState.artifacts
          .filter((a: Artifact) => a.artifactType.id === event.artifactType.id)
          .map(async (a: Artifact) => {
            await traceDataStore.updateTraceDataFromApi({
              artifactService: artifactService,
              projectId: parseInt(route.params.projectId as string),
              snapshotId: asSnapshotReference(route.params.snapshotId),
              filterId: parseIntUndefined(route.query.filter as string),
              id: a.id,
              direction: 'any',
            })
          }),
      )

      graphState.addToModel(traceDataStore.modelState).layout()
    } else {
      await Promise.all(
        traceDataStore.modelState.artifacts
          .filter((a: Artifact) => a.artifactType.id === event.artifactType.id)
          .map(async (a: Artifact) => {
            await traceDataStore.setTraceDataFromApi({
              artifactService: artifactService,
              projectId: parseInt(route.params.projectId as string),
              snapshotId: asSnapshotReference(route.params.snapshotId),
              filterId: parseIntUndefined(route.query.filter as string),
              id: a.id,
              direction: 'any',
            })
          }),
      )

      // Get the links for the resulting artifacts
      await Promise.all(
        traceDataStore.modelState.artifacts.map(async (a: Artifact) => {
          await traceDataStore.updateTraceDataFromApi({
            artifactService: artifactService,
            projectId: parseInt(route.params.projectId as string),
            snapshotId: asSnapshotReference(route.params.snapshotId),
            filterId: parseIntUndefined(route.query.filter as string),
            id: a.id,
            direction: 'any',
            excludeArtifacts: true,
          })
        }),
      )

      graphState?.filterModel(traceDataStore.modelState)
      graphState.layout()
    }
  }

  function registerLoadMorePill(graph: GraphState | undefined) {
    graphState = graph
  }

  useEventBus('loadArtifactsFromApiAfterPillPress', freezeWrap(tryWrap(handler), rappidContext))

  return { registerLoadMorePill }
}
